import { useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_WEEKLY_GOAL_MIN,
  allQuests,
  computeStreak,
  computeVitality,
  goalProgress,
  levelFromPoints,
  activeDaysOfWeek,
  energyLevel,
  lifetimeStats,
  pickInsight,
  questPoints,
  totalPoints,
  weeklySummaries,
  whoProgress,
} from '@movmania/core'
import { useStore } from '../store/useStore.js'

/**
 * Tout l'état affiché par l'app, dérivé du journal d'activités via
 * @movmania/core. Aucune règle métier n'est recalculée ici : l'app se contente
 * de lire. C'est ce qui garantira que le backoffice affichera les mêmes chiffres.
 */
export function useGame() {
  const activities = useStore((s) => s.activities)
  const profile = useStore((s) => s.profile)
  const referrals = useStore((s) => s.referrals)
  const weeklyGoalMin = profile?.weeklyGoalMin ?? DEFAULT_WEEKLY_GOAL_MIN

  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000)
    return () => clearInterval(id)
  }, [])

  return useMemo(() => {
    void tick
    const now = new Date()
    const summaries = weeklySummaries(activities, { weeks: 12, now })
    const week = summaries[summaries.length - 1]!
    const streak = computeStreak(summaries, weeklyGoalMin)
    const stats = lifetimeStats(activities)
    const vitality = computeVitality(activities, now)

    const quests = allQuests({
      activities,
      weeklyGoalMin,
      ...(profile?.favoriteActivities
        ? { favoriteActivities: profile.favoriteActivities }
        : {}),
      now,
    })

    const points =
      totalPoints({ activities, summaries, weeklyGoalMin, referrals }) +
      questPoints(quests)

    return {
      now,
      weeklyGoalMin,
      summaries,
      week,
      stats,
      goalProgress: goalProgress(week, weeklyGoalMin),
      whoProgress: whoProgress(week),
      streak,
      points,
      levelInfo: levelFromPoints(points),
      quests,
      dailyQuests: quests.filter((q) => q.scope === 'daily'),
      weeklyQuests: quests.filter((q) => q.scope === 'weekly'),
      /** Forme du moment, jamais affichée en chiffre. */
      momentum: vitality,
      /** Palier du compagnon (0-4). Aucun palier négatif. */
      energy: energyLevel(vitality),
      weekDays: activeDaysOfWeek(activities, now),
      insight: pickInsight({
        activities,
        summaries,
        weeklyGoalMin,
        currentStreak: streak.current,
        bestStreak: streak.best,
        now,
      }),
    }
  }, [activities, weeklyGoalMin, referrals, profile, tick])
}

export type Game = ReturnType<typeof useGame>
